import { SkyText } from "@skypointcloud/skypoint-ui";
import type { OverviewAlert, OverviewRuns } from "@shared/types/overview";
import { ValidationWarningIcon } from "@/lib/icons";
import { ROUTES } from "@/lib/routes";
import { OverviewCard, OverviewLink, statusTone } from "./OverviewCard";
import { QuietMessageBar } from "./QuietMessageBar";
import { alertIntent, alertKey, alertMessage, alertsEmptySentence } from "./overviewFormatting";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Alerts (contract §3, card 7): one bar per open failure, each with "Open run".
 *
 * The endpoint decides what is open: a failure a later success of the same source fixed is not
 * an alert (P1, SF11), and the list is already newest first and capped at four. The same run can
 * carry a run alert and a tests alert, so the key is kind + run id (P1, request 6).
 *
 * Empty reads "No failures in the last 7 days.", or "No open failures." when the week had
 * failures and every one was fixed later.
 */
export function AlertsCard({
  alerts,
  runs,
}: {
  alerts: OverviewAlert[];
  runs: OverviewRuns | null;
}) {
  const zone = useDisplayTz();
  return (
    <OverviewCard
      id="alerts"
      title="Alerts"
      icon={ValidationWarningIcon}
      tone={statusTone({
        failed: alerts.filter((alert) => alertIntent(alert) === "error").length,
        warning: alerts.length,
        // No open alert is the good news this card exists to give.
        ok: 1,
      })}
      testId="overview-alerts"
    >
      {alerts.length === 0 ? (
        <SkyText size={300} color="muted" block testId="overview-alerts-empty">
          {alertsEmptySentence(runs)}
        </SkyText>
      ) : (
        <ul className="flex flex-col gap-2">
          {alerts.map((alert) => {
            const message = alertMessage(alert);
            const when = `${alert.kind === "run_failed" || alert.kind === "run_partial" ? "Run at" : "Transformation step at"} ${formatInstant(alert.at, zone, "short", { named: true })}`;
            return (
              <li key={alertKey(alert)} data-testid={`overview-alert-${alertKey(alert)}`}>
                <QuietMessageBar
                  intent={alertIntent(alert)}
                  layout="multiline"
                  title={alert.title}
                  message={
                    <>
                      {message ? (
                        // An error code is one unbroken word (JDBC_PREFLIGHT_VALIDATION_FAILED): let
                        // it wrap so it cannot push the card past a phone's width (B1).
                        <SkyText size={200} block className="break-words">
                          {message}
                        </SkyText>
                      ) : null}
                      <SkyText size={200} color="muted" block>
                        {when}
                      </SkyText>
                    </>
                  }
                  actions={
                    <OverviewLink
                      to={`${ROUTES.runs}/${alert.runId}`}
                      testId={`overview-alert-open-${alertKey(alert)}`}
                      ariaLabel={`Open run: ${alert.title}`}
                    >
                      Open run
                    </OverviewLink>
                  }
                />
              </li>
            );
          })}
        </ul>
      )}
    </OverviewCard>
  );
}
